import React, { Component } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Download, TrendingUp, Target, PieChart as PieChartIcon } from 'lucide-react';
// --- Mock Data ---
const gwpByGeography = [{
  name: 'London',
  value: 4500,
  percentage: 35,
  color: '#3B82F6'
}, {
  name: 'South East',
  value: 3200,
  percentage: 25,
  color: '#10B981'
}, {
  name: 'North West',
  value: 2100,
  percentage: 16,
  color: '#F59E0B'
}, {
  name: 'Scotland',
  value: 1600,
  percentage: 12,
  color: '#8B5CF6'
}, {
  name: 'Midlands',
  value: 900,
  percentage: 7,
  color: '#EC4899'
}, {
  name: 'Other',
  value: 700,
  percentage: 5,
  color: '#6B7280'
}];
const gwpByOccupancy = [{
  name: 'Office',
  value: 3800,
  percentage: 29,
  color: '#3B82F6'
}, {
  name: 'Retail',
  value: 2900,
  percentage: 22,
  color: '#10B981'
}, {
  name: 'Manufacturing',
  value: 2400,
  percentage: 19,
  color: '#F59E0B'
}, {
  name: 'Warehousing',
  value: 1800,
  percentage: 14,
  color: '#8B5CF6'
}, {
  name: 'Hospitality',
  value: 1200,
  percentage: 9,
  color: '#EC4899'
}, {
  name: 'Other',
  value: 900,
  percentage: 7,
  color: '#6B7280'
}];
const newVsRenewalData = [{
  quarter: 'Q1 2024',
  newBusiness: 2800,
  renewal: 5200
}, {
  quarter: 'Q2 2024',
  newBusiness: 3200,
  renewal: 5800
}, {
  quarter: 'Q3 2024',
  newBusiness: 3600,
  renewal: 6100
}, {
  quarter: 'Q4 2024',
  newBusiness: 4100,
  renewal: 6500
}];
const growthVsPlanData = [{
  month: 'Jan',
  actual: 8.2,
  plan: 10.0
}, {
  month: 'Feb',
  actual: 9.5,
  plan: 10.0
}, {
  month: 'Mar',
  actual: 11.2,
  plan: 10.0
}, {
  month: 'Apr',
  actual: 10.8,
  plan: 10.0
}, {
  month: 'May',
  actual: 12.5,
  plan: 10.0
}, {
  month: 'Jun',
  actual: 13.8,
  plan: 10.0
}, {
  month: 'Jul',
  actual: 12.2,
  plan: 10.0
}, {
  month: 'Aug',
  actual: 14.5,
  plan: 10.0
}, {
  month: 'Sep',
  actual: 15.2,
  plan: 10.0
}, {
  month: 'Oct',
  actual: 13.8,
  plan: 10.0
}, {
  month: 'Nov',
  actual: 16.1,
  plan: 10.0
}, {
  month: 'Dec',
  actual: 17.5,
  plan: 10.0
}];
// --- Colors ---
const COLORS = {
  newBusiness: '#10B981',
  renewal: '#3B82F6',
  actual: '#10B981',
  plan: '#6B7280' // Gray
};
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1865">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1866">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1 last:mb-0" data-id="element-1867">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color || entry.fill
        }} data-id="element-1868" />
            <span className="text-gray-600" data-id="element-1869">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-1870">
              {entry.value.toLocaleString()}
              {entry.unit || ''}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
const CustomPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percentage,
  name
}: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-medium" data-id="element-1871">
      {`${name} (${percentage}%)`}
    </text>;
};
const DonutChart = ({
  data,
  title,
  total
}: {
  data: any[];
  title: string;
  total: string;
}) => {
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-96" data-id="element-1872">
      <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1873">{title}</h2>
      <ResponsiveContainer width="100%" height="100%" data-id="element-1874">
        <PieChart data-id="element-1875">
          <Pie data={data} cx="50%" cy="50%" labelLine={false} label={CustomPieLabel} outerRadius={80} innerRadius={50} fill="#8884d8" dataKey="value" paddingAngle={2} data-id="element-1876">
            {data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1877" />)}
          </Pie>
          <Tooltip content={<CustomTooltip data-id="element-1879" />} data-id="element-1878" />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" data-id="element-1880">
        <div className="text-2xl font-bold text-gray-900" data-id="element-1881">{total}</div>
        <div className="text-xs text-gray-500 mt-1" data-id="element-1882">Total GWP</div>
      </div>
    </div>;
};
const LegendItem = ({
  color,
  label,
  value,
  percentage
}: {
  color: string;
  label: string;
  value: string;
  percentage: number;
}) => <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-1883">
    <div className="flex items-center gap-2" data-id="element-1884">
      <div className="w-3 h-3 rounded-full" style={{
      backgroundColor: color
    }} data-id="element-1885"></div>
      <span className="text-sm text-gray-700" data-id="element-1886">{label}</span>
    </div>
    <div className="text-right" data-id="element-1887">
      <span className="text-sm font-semibold text-gray-900" data-id="element-1888">{value}</span>
      <span className="text-xs text-gray-500 ml-2" data-id="element-1889">({percentage}%)</span>
    </div>
  </div>;
export function PortfolioMixGrowth() {
  const totalGWP = gwpByGeography.reduce((sum, item) => sum + item.value, 0);
  const ytdGrowth = 17.5; // From last month of growthVsPlanData
  const planGrowth = 10.0;
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1890">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1891">
        <div data-id="element-1892">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1893">
            Portfolio Mix & Growth
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1894">
            Diversification and strategic alignment • FY 2024
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-1895">
          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full" data-id="element-1896">
            <TrendingUp className="text-green-600" size={16} data-id="element-1897" />
            <span className="text-sm font-medium text-green-700" data-id="element-1898">
              +{ytdGrowth}% YTD Growth
            </span>
          </div>
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-1899">
            <Download size={16} className="mr-2" data-id="element-1900" />
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1901">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1902">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1903">
            Total GWP
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1904">
            £{(totalGWP / 1000).toFixed(1)}M
          </p>
          <p className="text-xs text-green-600 mt-1" data-id="element-1905">
            +{ytdGrowth}% vs prior year
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1906">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1907">
            Growth vs Plan
          </p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1908">
            +{(ytdGrowth - planGrowth).toFixed(1)}%
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1909">
            Ahead of {planGrowth}% target
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1910">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1911">
            Renewal Rate
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1912">87%</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1913">Strong retention</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1914">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1915">
            New Business
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1916">£3.7M</p>
          <p className="text-xs text-green-600 mt-1" data-id="element-1917">+24% vs Q4 2023</p>
        </div>
      </div>

      {/* Top Row: Two Donut Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1918">
        {/* GWP by Geography */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1919">
          <div className="flex items-center justify-between mb-4" data-id="element-1920">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1921">
              GWP by Geography
            </h2>
            <PieChartIcon className="text-gray-400" size={20} data-id="element-1922" />
          </div>
          <div className="relative h-64" data-id="element-1923">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1924">
              <PieChart data-id="element-1925">
                <Pie data={gwpByGeography} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" dataKey="value" paddingAngle={2} data-id="element-1926">
                  {gwpByGeography.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1927" />)}
                </Pie>
                <Tooltip content={<CustomTooltip data-id="element-1929" />} data-id="element-1928" />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" data-id="element-1930">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1931">
                £{(totalGWP / 1000).toFixed(1)}M
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-1932">Total</div>
            </div>
          </div>
          <div className="mt-6 space-y-1" data-id="element-1933">
            {gwpByGeography.map((item, index) => <LegendItem key={index} color={item.color} label={item.name} value={`£${(item.value / 1000).toFixed(1)}M`} percentage={item.percentage} data-id="element-1934" />)}
          </div>
        </div>

        {/* GWP by Occupancy */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1935">
          <div className="flex items-center justify-between mb-4" data-id="element-1936">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1937">
              GWP by Occupancy
            </h2>
            <PieChartIcon className="text-gray-400" size={20} data-id="element-1938" />
          </div>
          <div className="relative h-64" data-id="element-1939">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1940">
              <PieChart data-id="element-1941">
                <Pie data={gwpByOccupancy} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" dataKey="value" paddingAngle={2} data-id="element-1942">
                  {gwpByOccupancy.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1943" />)}
                </Pie>
                <Tooltip content={<CustomTooltip data-id="element-1945" />} data-id="element-1944" />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" data-id="element-1946">
              <div className="text-2xl font-bold text-gray-900" data-id="element-1947">
                £{(totalGWP / 1000).toFixed(1)}M
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-1948">Total</div>
            </div>
          </div>
          <div className="mt-6 space-y-1" data-id="element-1949">
            {gwpByOccupancy.map((item, index) => <LegendItem key={index} color={item.color} label={item.name} value={`£${(item.value / 1000).toFixed(1)}M`} percentage={item.percentage} data-id="element-1950" />)}
          </div>
        </div>
      </div>

      {/* Bottom Row: Two Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1951">
        {/* New Business vs Renewal */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1952">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1953">
            New Business vs Renewal
          </h2>
          <div className="h-64" data-id="element-1954">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1955">
              <BarChart data={newVsRenewalData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-1956">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1957" />
                <XAxis dataKey="quarter" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-1958" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} tickFormatter={value => `£${value / 1000}M`} data-id="element-1959" />
                <Tooltip content={<CustomTooltip data-id="element-1961" />} data-id="element-1960" />
                <Legend verticalAlign="top" align="right" wrapperStyle={{
                paddingBottom: '20px'
              }} iconType="circle" data-id="element-1962" />
                <Bar dataKey="renewal" name="Renewal" fill={COLORS.renewal} radius={[4, 4, 0, 0]} data-id="element-1963" />
                <Bar dataKey="newBusiness" name="New Business" fill={COLORS.newBusiness} radius={[4, 4, 0, 0]} data-id="element-1964" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-1965">
            New business represents{' '}
            <span className="font-semibold text-gray-900" data-id="element-1966">28%</span> of total
            GWP in Q4
          </p>
        </div>

        {/* Growth vs Plan */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1967">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1968">
            Growth vs Plan
          </h2>
          <div className="h-64" data-id="element-1969">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1970">
              <LineChart data={growthVsPlanData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-1971">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1972" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-1973" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} tickFormatter={value => `${value}%`} domain={[0, 20]} data-id="element-1974" />
                <Tooltip content={<CustomTooltip data-id="element-1976" />} data-id="element-1975" />
                <Legend verticalAlign="top" align="right" wrapperStyle={{
                paddingBottom: '20px'
              }} iconType="line" data-id="element-1977" />
                <ReferenceLine y={10} stroke={COLORS.plan} strokeDasharray="5 5" label={{
                value: 'Plan (10%)',
                position: 'right',
                fill: '#6B7280',
                fontSize: 12
              }} data-id="element-1978" />
                <Line type="monotone" dataKey="actual" name="Actual Growth" stroke={COLORS.actual} strokeWidth={3} dot={{
                r: 4
              }} activeDot={{
                r: 6
              }} data-id="element-1979" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-1980">
            Consistently exceeding plan target by{' '}
            <span className="font-semibold text-green-600" data-id="element-1981">+7.5%</span> on
            average
          </p>
        </div>
      </div>
    </div>;
}